// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the Situation room (docs/AYNI_XR_CONTROL_ROOM_PLAN.md): what the
 * browser and the server agree on. One situation room per Situation Room
 * licence (`situation` segment), keyed by a hash of the licence id like a
 * Lab space; it keeps:
 *
 * - **the board** (CR1): a `briefing` Khipu whose timed steps a wall display
 *   plays in a loop, each step on the latest frame;
 * - **wall displays** (CR1, decision CR-D2: no seat): browsers paired by a
 *   short code that show the board, or the briefing meeting put on the wall;
 * - **the briefing on the wall**: a meeting's audience link while it runs;
 * - **own layers** (CR5, files only): the organisation's GeoJSON (KML is
 *   turned into GeoJSON in the browser), private to the licence and its walls.
 *
 * Pure: no DOM, no fetch. The server checks every write with these
 * functions; the browser uses them before sending.
 */

import { cleanText } from './research/labSpace'
import { normalizeKhipu } from './khipuFile'
import type { Khipu } from './khipu'
import type { XrEntitlements } from './xrPlans'

/** Whether a plan has the Situation room: the Situation Room plans. */
export function situationAllowed(ent: Pick<XrEntitlements, 'plan'>): boolean {
  return ent.plan?.segment === 'situation'
}

// ------------------------------------------------------------------ limits

/** Paired wall displays per situation room (they hold no seat, CR-D2). */
export const SITUATION_MAX_DISPLAYS = 4
/** Own layers per situation room. */
export const SITUATION_MAX_LAYERS = 10
/** One layer's GeoJSON as stored (a D1 row holds at most 2 MB). */
export const SITUATION_LAYER_MAX_BYTES = 1_500_000
/** Features in one layer. */
export const SITUATION_LAYER_MAX_FEATURES = 20_000
/** The board as stored. */
export const SITUATION_BOARD_MAX_BYTES = 256 * 1024
/** Steps on the board. */
export const SITUATION_BOARD_MAX_STEPS = 40
/** How long a pairing code shown on a wall stays good. */
export const PAIR_CODE_TTL_MS = 10 * 60 * 1000
/** A briefing put on the wall comes off by itself after this long at most. */
export const BRIEFING_ON_WALL_MAX_MS = 8 * 60 * 60 * 1000

/** Pairing codes: no 0/O, 1/I/L, so a code read off a wall across a room is typed right. */
export const PAIR_CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789'
export const PAIR_CODE_LENGTH = 6

/** A code as typed (any case, spaces and dashes) as stored, or null when it cannot be one. */
export function normalizePairCode(input: unknown): string | null {
  if (typeof input !== 'string') return null
  const code = input.toUpperCase().replace(/[\s-]/g, '')
  if (code.length !== PAIR_CODE_LENGTH) return null
  for (const c of code) if (!PAIR_CODE_ALPHABET.includes(c)) return null
  return code
}

/** A code as a wall shows it: ABC-234. */
export function formatPairCode(code: string): string {
  return `${code.slice(0, 3)}-${code.slice(3)}`
}

// ------------------------------------------------------------------- types

export interface SituationDisplayInfo {
  id: string
  name: string
  pairedAt: number
  /** When the display last asked for the board, epoch ms; 0 never. */
  lastSeenAt: number
}

export interface SituationLayerInfo {
  id: string
  title: string
  features: number
  bytes: number
  createdAt: number
}

/** A briefing meeting shown on the walls instead of the board, until it ends. */
export interface SituationBriefing {
  /** The meeting's code. */
  code: string
  /** The meeting's audience link, a path on this site (`/?room=…`). */
  audience: string
  /** When the walls go back to the board, epoch ms. */
  until: number
}

/** `GET /api/situation`, for the licence's members. */
export interface SituationState {
  board: Khipu | null
  boardUpdatedAt: number
  displays: SituationDisplayInfo[]
  layers: SituationLayerInfo[]
  briefing: SituationBriefing | null
  /** The licence's end plus 30 days: then the room and everything in it goes. */
  keptUntil: number
  limits: { displays: number; layers: number; layerBytes: number }
}

/** `GET /api/situation/wall`, for a paired display (its token). */
export interface WallState {
  /** The display's own name, as the room's members called it. */
  name: string
  board: Khipu | null
  boardUpdatedAt: number
  layers: SituationLayerInfo[]
  briefing: SituationBriefing | null
  /**
   * The stream packages the licence covers, lent to the display (it has no
   * account): package ids, or `['*']` for every package (the Situation
   * plans' `all-hazards`).
   */
  packages: string[]
  /** Globes side by side the licence allows. */
  globes: number
}

/** `POST /api/situation/pair` (a wall asks for a code) → 201. */
export interface PairStart {
  code: string
  /** A secret only this wall knows, to collect its token. */
  poll: string
  expiresAt: number
}

/** `GET /api/situation/pair?code=&poll=`. */
export type PairPoll =
  | { state: 'waiting'; expiresAt: number }
  /** Given once: the code is gone afterwards. */
  | { state: 'paired'; token: string; displayId: string; name: string }

// -------------------------------------------------------------- validators

/** A display's name, or null. */
export function checkDisplayName(raw: unknown): string | null {
  const name = cleanText(raw, 60)
  return name ? name : null
}

/** A layer's title, or null. */
export function checkLayerTitle(raw: unknown): string | null {
  const title = cleanText(raw, 80)
  return title ? title : null
}

/**
 * The board as stored: a `briefing` Khipu reduced to what a board plays
 * (a template, not a record): no log, no creator, attribution or editorial
 * notes, nothing personal (review, 2026-10-11: only listed fields are
 * kept), within the size limits.
 */
export function checkBoard(raw: unknown): Khipu | 'invalid' | 'too_large' {
  const k = normalizeKhipu(raw)
  if (!k || k.kind !== 'briefing') return 'invalid'
  if (k.path.steps.length > SITUATION_BOARD_MAX_STEPS) return 'too_large'
  const board: Khipu = {
    format: 'ayni-khipu',
    schemaVersion: k.schemaVersion,
    id: k.id,
    kind: 'briefing',
    status: 'published',
    title: k.title,
    ...(Array.isArray(k.languages) ? { languages: k.languages.filter((l) => typeof l === 'string').slice(0, 8) } : {}),
    created: k.created,
    ...(typeof k.modified === 'string' ? { modified: k.modified } : {}),
    ...(k.description ? { description: k.description } : {}),
    privacy: { mode: 'anonymous' },
    datasets: k.datasets,
    path: { steps: k.path.steps },
  }
  if (new TextEncoder().encode(JSON.stringify(board)).length > SITUATION_BOARD_MAX_BYTES) return 'too_large'
  return board
}

const MEETING_CODE = /^[A-Z0-9]{4,12}$/

/**
 * A briefing to put on the walls: the meeting's code, its audience link
 * (only a path on this site that opens that room) and when it ends,
 * capped at eight hours from now. Whether the meeting exists, or who hosts
 * it, is not checked: any member may point the room's own walls at any
 * meeting, as anyone may open an audience link (the walls then follow it
 * as one more person in its audience).
 */
export function checkBriefing(raw: unknown, nowMs: number): SituationBriefing | null {
  if (typeof raw !== 'object' || raw === null) return null
  const r = raw as Record<string, unknown>
  if (typeof r.code !== 'string' || !MEETING_CODE.test(r.code)) return null
  if (typeof r.audience !== 'string' || r.audience.length > 512 || !r.audience.startsWith('/?')) return null
  let url: URL
  try {
    url = new URL(r.audience, 'https://site.invalid')
  } catch {
    return null
  }
  if (url.origin !== 'https://site.invalid' || url.pathname !== '/' || url.searchParams.get('room') !== r.code) return null
  const until = typeof r.until === 'number' && Number.isFinite(r.until) ? r.until : 0
  if (until <= nowMs) return null
  return { code: r.code, audience: `/${url.search}`, until: Math.min(until, nowMs + BRIEFING_ON_WALL_MAX_MS) }
}

type Position = [number, number]
const GEOMETRY_TYPES = new Set(['Point', 'MultiPoint', 'LineString', 'MultiLineString', 'Polygon', 'MultiPolygon'])

export type LayerCheck =
  | { ok: true; geojson: { type: 'FeatureCollection'; features: unknown[] }; features: number; bytes: number }
  | { ok: false; error: 'invalid' | 'empty' | 'too_large' | 'too_many_features' }

function position(v: unknown): Position | null {
  if (!Array.isArray(v) || v.length < 2) return null
  const [lon, lat] = v
  if (typeof lon !== 'number' || typeof lat !== 'number' || !Number.isFinite(lon) || !Number.isFinite(lat)) return null
  if (lon < -180 || lon > 180 || lat < -90 || lat > 90) return null
  // Six decimals is 11 cm: enough for a building, and it keeps the layer small.
  return [Math.round(lon * 1e6) / 1e6, Math.round(lat * 1e6) / 1e6]
}

function positions(v: unknown, depth: number): unknown | null {
  if (depth === 0) return position(v)
  if (!Array.isArray(v) || v.length === 0) return null
  const out: unknown[] = []
  for (const item of v) {
    const p = positions(item, depth - 1)
    if (p === null) return null
    out.push(p)
  }
  return out
}

const DEPTH: Record<string, number> = { Point: 0, MultiPoint: 1, LineString: 1, MultiLineString: 2, Polygon: 2, MultiPolygon: 3 }

/**
 * One own layer as stored: a FeatureCollection (a bare Feature or geometry
 * is wrapped) of points, lines and polygons in WGS 84 longitude/latitude,
 * each feature keeping only a `name` and a `description` as plain text
 * (nothing else the file carried is stored or shown).
 */
export function checkLayerGeoJson(raw: unknown): LayerCheck {
  if (typeof raw !== 'object' || raw === null) return { ok: false, error: 'invalid' }
  const r = raw as Record<string, unknown>
  let items: unknown[]
  if (r.type === 'FeatureCollection' && Array.isArray(r.features)) items = r.features
  else if (r.type === 'Feature') items = [r]
  else if (typeof r.type === 'string' && GEOMETRY_TYPES.has(r.type)) items = [{ type: 'Feature', geometry: r, properties: {} }]
  else return { ok: false, error: 'invalid' }
  if (items.length > SITUATION_LAYER_MAX_FEATURES) return { ok: false, error: 'too_many_features' }
  const features: unknown[] = []
  for (const item of items) {
    if (typeof item !== 'object' || item === null) return { ok: false, error: 'invalid' }
    const f = item as Record<string, unknown>
    const g = f.geometry as Record<string, unknown> | null | undefined
    // A feature without a geometry is legal GeoJSON and shows nothing: skip it.
    if (g === null || g === undefined) continue
    if (typeof g !== 'object' || typeof g.type !== 'string' || !GEOMETRY_TYPES.has(g.type)) return { ok: false, error: 'invalid' }
    const coordinates = positions(g.coordinates, DEPTH[g.type])
    if (coordinates === null) return { ok: false, error: 'invalid' }
    const props = (typeof f.properties === 'object' && f.properties !== null ? f.properties : {}) as Record<string, unknown>
    const properties: Record<string, string> = {}
    const name = cleanText(props.name ?? props.Name ?? props.title, 120)
    if (name) properties.name = name
    const description = cleanText(props.description ?? props.Description, 500, { multiline: true })
    if (description) properties.description = description
    features.push({ type: 'Feature', geometry: { type: g.type, coordinates }, properties })
  }
  if (features.length === 0) return { ok: false, error: 'empty' }
  const geojson = { type: 'FeatureCollection' as const, features }
  const bytes = new TextEncoder().encode(JSON.stringify(geojson)).length
  if (bytes > SITUATION_LAYER_MAX_BYTES) return { ok: false, error: 'too_large' }
  return { ok: true, geojson, features: features.length, bytes }
}
